/**
 * ExternalRecipientsEditor: the "Additional recipients" block shared by the
 * weekly org digest and the admins digest on Settings > Notifications.
 *
 * Outside addresses (no ProgramRow account) that receive a digest. Purely a
 * controlled list editor: the parent form owns the array and saves it with
 * everything else. Validation mirrors the server's element schema
 * (organization.schema.ts) so a bad address fails here with a specific
 * message instead of 400-ing the whole settings save.
 */

import { useState } from 'react';
import { z } from 'zod';
import type { DigestExternalRecipient } from '../../api/organizations';

export const MAX_EXTERNAL_RECIPIENTS = 50;
const externalEmailSchema = z.string().trim().toLowerCase().email().max(254);
const MAX_RECIPIENT_NAME_LENGTH = 100;

interface ExternalRecipientsEditorProps {
  recipients: DigestExternalRecipient[];
  onChange: (next: DigestExternalRecipient[]) => void;
  /** The owning feature is switched off: inputs render but are disabled. */
  disabled: boolean;
  /** Org owners edit; everyone else sees the list read-only. */
  canEdit: boolean;
  /** Explains what these people receive. */
  description: string;
  /** Prefix for the input aria-labels, so two editors on one page stay distinct. */
  ariaPrefix?: string;
}

export function ExternalRecipientsEditor({
  recipients,
  onChange,
  disabled,
  canEdit,
  description,
  ariaPrefix = 'Additional recipient',
}: ExternalRecipientsEditorProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const trimmedName = name.trim();
    const normalizedEmail = email.trim().toLowerCase();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    if (trimmedName.length > MAX_RECIPIENT_NAME_LENGTH) {
      setError(`Name must be ${MAX_RECIPIENT_NAME_LENGTH} characters or fewer`);
      return;
    }
    if (!externalEmailSchema.safeParse(normalizedEmail).success) {
      setError('Enter a valid email address');
      return;
    }
    if (recipients.some((r) => r.email.toLowerCase() === normalizedEmail)) {
      setError('That email is already on the list');
      return;
    }
    if (recipients.length >= MAX_EXTERNAL_RECIPIENTS) {
      setError(`At most ${MAX_EXTERNAL_RECIPIENTS} additional recipients`);
      return;
    }
    onChange([...recipients, { name: trimmedName, email: normalizedEmail }]);
    setName('');
    setEmail('');
    setError(null);
  };

  const remove = (index: number) => {
    onChange(recipients.filter((_, i) => i !== index));
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900">Additional recipients</h3>
      <p className="text-sm text-gray-500 mt-1 mb-3">{description}</p>

      {canEdit && (
        <div className="flex flex-wrap items-start gap-2 mb-3">
          <input
            type="text"
            aria-label={`${ariaPrefix} name`}
            placeholder="Name"
            value={name}
            disabled={disabled}
            onChange={(e) => setName(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <input
            type="email"
            aria-label={`${ariaPrefix} email`}
            placeholder="Email"
            value={email}
            disabled={disabled}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={add}
            disabled={disabled}
            className="px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
      )}
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {recipients.length === 0 ? (
        <p className="text-sm text-gray-500">No additional recipients.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {recipients.map((recipient, index) => (
            <li
              key={`${recipient.email}-${index}`}
              className="py-2 flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <span className="text-sm font-medium text-gray-900">{recipient.name}</span>
                <span className="ml-2 text-sm text-gray-500 break-all">{recipient.email}</span>
              </div>
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Remove ${recipient.name}`}
                  onClick={() => remove(index)}
                  className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-50 flex-shrink-0"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}
